import "./compact.css";
import { useMemo } from "react";
import type { Concept } from "@/concepts/types";
import { ConsoleTaskDetail } from "@/concepts/console/TaskDetail";
import { ConsoleTaskList } from "@/concepts/console/TaskList";
import { ConsoleTaskRow } from "@/concepts/console/TaskRow";
import { ConceptOverride, useConcept } from "@/features/concept-provider";
import { TaskListView, type TaskListViewProps } from "@/features/list/TaskListView";
import { DetailBody, DetailSheet, useOpenTaskModel } from "@/features/task/TaskDetailHost";
import { useElementWidth } from "@/lib/use-media";

/** Below this width the list and a 400px panel can't sit side by side; detail opens as a sheet. */
const SIDE_BY_SIDE_MIN = 780;

/**
 * The Compact view mode: Console's dense rows, status grouping, and right-hand detail panel, rendered
 * inside the current shell with its palette (Console's components only read theme tokens).
 */
export function CompactView(props: TaskListViewProps) {
  const { concept } = useConcept();
  const compact = useMemo<Concept>(
    () => ({ ...concept, TaskRow: ConsoleTaskRow, TaskList: ConsoleTaskList, TaskDetail: ConsoleTaskDetail, detailMode: "panel", rowHeight: 36 }),
    [concept],
  );
  const { via, task } = useOpenTaskModel();
  const inList = !!task && props.groups.some((g) => g.tasks.some((t) => t.id === task.id));
  const panelTask = via === "list" && inList ? task : null;
  const [measure, width] = useElementWidth<HTMLDivElement>();
  const asSheet = width > 0 && width < SIDE_BY_SIDE_MIN;
  return (
    <ConceptOverride concept={compact}>
      <div ref={measure} data-wide data-compact className="desk-compact flex items-start gap-6">
        <div className="@container/main min-w-0 flex-1 px-4">
          <TaskListView {...props} />
        </div>
        {panelTask && asSheet && <DetailSheet key={panelTask.id} task={panelTask} />}
        {panelTask && !asSheet && (
          <aside
            aria-label="Task detail"
            className="sticky top-4 flex h-[max(420px,calc(100vh-15rem))] w-[400px] shrink-0 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_2px_rgba(37,36,34,0.04),0_12px_32px_-20px_rgba(37,36,34,0.25)] dark:shadow-none"
          >
            <DetailBody key={panelTask.id} task={panelTask} mode="panel" />
          </aside>
        )}
      </div>
    </ConceptOverride>
  );
}
